import { useEffect, useState } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import { Modal } from "@impero-com/ui";

import { Footer } from "components/modals/Footer";
import { UserOrUserGroupOverview } from "utils/user";

import { ReassignSelection } from "../useControlActivityReassignUtils";

import { AssigneeField } from "./AssigneeField";

interface ControlActivityReassignModalProps {
    open: boolean;
    loading: boolean;
    /** Number of reviewer steps in the (homogeneous) selection: 0, 1, or 2. */
    reviewerCount: number;
    onConfirm: (selection: ReassignSelection) => void;
    onClose: () => void;
}

export function ControlActivityReassignModal({
    open,
    loading,
    reviewerCount,
    onConfirm,
    onClose,
}: ControlActivityReassignModalProps) {
    const { t } = useTranslation();
    const [responsible, setResponsible] = useState<UserOrUserGroupOverview>();
    const [firstReviewer, setFirstReviewer] =
        useState<UserOrUserGroupOverview>();
    const [secondReviewer, setSecondReviewer] =
        useState<UserOrUserGroupOverview>();

    // Reset the picks each time the modal opens.
    useEffect(() => {
        if (open) {
            /* eslint-disable react-hooks/set-state-in-effect */
            setResponsible(undefined);
            setFirstReviewer(undefined);
            setSecondReviewer(undefined);
            /* eslint-enable react-hooks/set-state-in-effect */
        }
    }, [open]);

    const hasFirstReviewer = reviewerCount >= 1;
    const hasSecondReviewer = reviewerCount >= 2;

    const selection: ReassignSelection = {
        ...(responsible && { responsible }),
        ...(hasFirstReviewer && firstReviewer && { firstReviewer }),
        ...(hasSecondReviewer && secondReviewer && { secondReviewer }),
    };

    // At least one role must be picked (an empty payload is rejected server-side).
    const hasAssignee = Object.keys(selection).length > 0;
    return (
        <Modal
            variant="content"
            open={open}
            footer={
                <Footer
                    onClose={onClose}
                    onConfirm={() => onConfirm(selection)}
                    confirmLabel={t("Reassign")}
                    loading={loading}
                    okDisabled={!hasAssignee}
                />
            }
            onClose={onClose}
            title={t("ControlResult_Reassign_PopupTitle")}
        >
            <AssigneeField
                id="reassign-responsible"
                label={t("ControlResult_Reassign_Responsible")}
                value={responsible}
                onSelect={setResponsible}
            />
            {hasFirstReviewer && (
                <AssigneeField
                    id="reassign-first-reviewer"
                    label={t("ControlResult_Reassign_Reviewer")}
                    value={firstReviewer}
                    onSelect={setFirstReviewer}
                />
            )}
            {hasSecondReviewer && (
                <AssigneeField
                    id="reassign-second-reviewer"
                    label={t("ControlResult_Reassign_Second_Reviewer")}
                    value={secondReviewer}
                    onSelect={setSecondReviewer}
                />
            )}
        </Modal>
    );
}
